import { useEffect, useState, ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAdminAuth } from '@/hooks/useAdminAuth';

interface AdminRouteProps {
  children: ReactNode;
}

export default function AdminRoute({ children }: AdminRouteProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthed, isAdmin } = useAdminAuth();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Wait a moment for auth state to initialize
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!isLoading) {
      // Not authenticated - redirect to signin
      if (!isAuthed) {
        const currentPath = location.pathname + location.search;
        navigate(`/auth/signin?redirect=${encodeURIComponent(currentPath)}`, { replace: true });
      }
      // Authenticated but not admin - redirect to library with access denied
      else if (!isAdmin) {
        navigate('/library', { replace: true });
      }
    }
  }, [isAuthed, isAdmin, isLoading, navigate, location]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Show access denied for authenticated non-admin users
  if (isAuthed && !isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-2">Access Denied</h1>
          <p className="text-gray-600">You do not have permission to access this page.</p>
        </div>
      </div>
    );
  }

  return isAuthed && isAdmin ? <>{children}</> : null;
}
